import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Box, HStack, Switch, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { selectCompanySettings, updateCompanySetting } from '../../../../redux/modules/company';

const CertificatesPanel = ({ props }) => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const companySettings = useSelector(selectCompanySettings);

  const agentDetailsSetting = useMemo(
    () => companySettings.find(setting => setting.name === 'require_agent_details'),
    [companySettings]
  );

  const autoArchiveDocumentSetting = useMemo(
    () => companySettings.find(setting => setting.name === 'auto_archive_expired_documents'),
    [companySettings]
  );

  const autoArchivePolicySetting = useMemo(
    () => companySettings.find(setting => setting.name === 'auto_archive_expired_policies'),
    [companySettings]
  );

  const [agentDetailsState, setAgentDetailsState] = useState(agentDetailsSetting?.value || false);
  const [autoArchiveDocumentState, setAutoArchiveDocumentState] = useState(autoArchiveDocumentSetting?.value || false);
  const [autoArchivePolicyState, setAutoArchivePolicyState] = useState(autoArchivePolicySetting?.value || false);

  const handleOnChange = (settingTypeId, value) => {
    dispatch(updateCompanySetting(
      companyId,
      settingTypeId,
      value,
      value
    ));
  };

  return (
    <Box layerStyle="tabPanel">
      <Box borderBottom="1px solid" borderBottomColor="greyScale.200" p={8}>
        <HStack justifyContent="space-between" mb={8}>
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Require Agent Details</Text>
            <Text color="greyScale.500" textStyle="bodySm">Turning this setting on will require agents to supply their license number and agency details when submitting certificates into PINS.</Text>
            <Text color="greyScale.500" textStyle="bodySm">Please contact our support team at support@pinsadvantage.com to enable or disable this setting.</Text>
          </Box>

          <Box>
            <Tooltip
              shouldWrapChildren
              label="Please contact support at support@pinsadvantage.com to enable/disable this setting"
              placement="left"
            >
              <Switch
                isDisabled // locked by support
                isChecked={!!agentDetailsState}
                onChange={e => {
                  setAgentDetailsState(e.target.checked);
                  handleOnChange(agentDetailsSetting.id, e.target.checked);
                }}
              />
            </Tooltip>
          </Box>
        </HStack>

        <HStack justifyContent="space-between" mb={8}>
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Auto Archive Expired Documents</Text>
            <Text color="greyScale.500" textStyle="bodySm">
              When enabled, each night, PINS will check for expired documents and automatically archive them if and only if
              another document of the same type is active and replaces all of the expired document&#39;s policy links and custom expiration dates.
              This helps keep your document list clean and up-to-date.
            </Text>
          </Box>

          <Box>
            <Switch
              isChecked={!!autoArchiveDocumentState}
              onChange={e => {
                setAutoArchiveDocumentState(e.target.checked);
                handleOnChange(autoArchiveDocumentSetting.id, e.target.checked);
              }}
            />
          </Box>
        </HStack>

        <HStack justifyContent="space-between">
          <Box pr={4} textAlign="left">
            <Text mb={4} textStyle="bodyMd500">Auto Archive Expired Policies</Text>
            <Text color="greyScale.500" textStyle="bodySm">
              When enabled, each night, PINS will check for expired policies and automatically archive them if and only if
              another policy of the same type is active and currently effective. This helps keep your policy list clean and up-to-date.
            </Text>
          </Box>

          <Box>
            <Switch
              isChecked={!!autoArchivePolicyState}
              onChange={e => {
                setAutoArchivePolicyState(e.target.checked);
                handleOnChange(autoArchivePolicySetting.id, e.target.checked);
              }}
            />
          </Box>
        </HStack>
      </Box>
    </Box>
  );
};

CertificatesPanel.propTypes = {};

export default CertificatesPanel;
